import { useCallback, useEffect, useRef, useState } from "react";
import RefreshButton from "../RefreshButton/RefreshButton";
import { usporadatelApi } from "../../services/usporadatelApi";
import { filterTopics, isTopicOverdue } from "../../utils/notificationTopics";
import "./Usporadatel.css";

const TABS = [
  { id: "topics", label: "Topics" },
  { id: "jobs", label: "Ingest jobs" },
  { id: "reconciliations", label: "Reconciliation" },
  { id: "wake-prompt", label: "Wake Prompt" },
];

const REFRESH_TITLES = {
  topics: "Refresh topics",
  jobs: "Refresh ingest jobs",
  reconciliations: "Refresh reconciliations",
  "wake-prompt": "Refresh wake prompt",
};

const TOPIC_FILTERS = [
  { id: "open", label: "Open" },
  { id: "new", label: "New" },
  { id: "critical", label: "Critical" },
  { id: "closed", label: "Closed" },
  { id: "all", label: "All" },
];

function formatTime(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function topicState(item) {
  return item?.topic || {};
}

function Badge({ children, tone = "neutral" }) {
  return <span className={`uspo-badge ${tone}`}>{children}</span>;
}

function TopicCard({ item, selected, onSelect }) {
  const topic = topicState(item);
  const overdue = isTopicOverdue(item);
  const critical = item.unread && topic.criticality === "critical";
  return (
    <button className={`uspo-topic-card${selected ? " selected" : ""}${item.unread ? " unread" : ""}${overdue ? " overdue" : ""}${critical ? " critical" : ""}`} onClick={onSelect}>
      <div className="uspo-card-heading">
        <strong>{topic.title || "Untitled topic"}</strong>
        <span>v{item.version ?? "?"}</span>
      </div>
      <p>{topic.status || topic.summary || "No status recorded"}</p>
      <div className="uspo-badges">
        {overdue && <Badge tone="critical">Overdue</Badge>}
        <Badge tone={item.unread ? "unread" : "neutral"}>{item.unread ? "New" : "Read"}</Badge>
        <Badge>{topic.lifecycle || "unknown"}</Badge>
        <Badge tone={critical ? "critical" : "neutral"}>{topic.criticality || "normal"}</Badge>
      </div>
      <div className="uspo-card-meta">
        <span>Due {formatTime(topic.dueAt)}</span>
        <span>Updated {formatTime(item.updatedAt)}</span>
      </div>
    </button>
  );
}

function TopicDetail({ detail, revisions, loading }) {
  if (loading) return <div className="placeholder">Loading...</div>;
  if (!detail) return <div className="uspo-empty">Select a topic to inspect its current state and revisions.</div>;
  const topic = topicState(detail);
  const source = detail.lastSource || {};
  const reconciliation = detail.lastReconciliation || {};
  return (
    <section className="uspo-detail" aria-label="Topic detail">
      <div className="uspo-detail-heading">
        <div>
          <span className="uspo-eyebrow">Current topic</span>
          <h2>{topic.title || "Untitled topic"}</h2>
        </div>
        <Badge>Version {detail.version ?? "?"}</Badge>
      </div>
      <p className="uspo-summary">{topic.summary || "No summary recorded."}</p>
      <dl className="uspo-facts">
        <div><dt>Lifecycle</dt><dd>{topic.lifecycle || "Not set"}</dd></div>
        <div><dt>Criticality</dt><dd>{topic.criticality || "Not set"}</dd></div>
        <div><dt>Unread</dt><dd>{detail.unread ? `Since ${formatTime(detail.unreadSince)}` : "No"}</dd></div>
        <div><dt>Due</dt><dd>{formatTime(topic.dueAt)}{topic.dueText ? ` (${topic.dueText})` : ""}</dd></div>
        <div><dt>Last source</dt><dd>{source.source ? `${source.source} · ${source.eventType || "event"}` : "Not set"}</dd></div>
        <div><dt>Last reconciliation</dt><dd>{reconciliation.runId ? `${reconciliation.operation} · ${formatTime(reconciliation.appliedAt)}` : "None"}</dd></div>
      </dl>
      <div className="uspo-history">
        <h3>Revision history</h3>
        {revisions.length === 0 ? <div className="uspo-empty compact">No prior revisions.</div> : revisions.map((revision) => {
          const state = topicState(revision);
          return (
            <div className="uspo-revision" key={revision.SK || revision.version}>
              <div><strong>v{revision.version}</strong><span>{formatTime(revision.updatedAt)}</span></div>
              <p>{state.status || state.summary || "No status recorded"}</p>
              <span>{state.lifecycle || "unknown"} · {state.criticality || "normal"} · {revision.unread ? "unread" : "read"}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function JobList({ jobs }) {
  if (!jobs.length) return <div className="uspo-empty">No ingest attempts recorded.</div>;
  return <div className="uspo-ops-list">{jobs.map((job) => (
    <article className="item-card uspo-job" key={`${job.messageId}-${job.receiveCount}`}>
      <div className="uspo-card-heading"><strong>{job.source || "Unparsed event"}</strong><Badge tone={job.status === "failed" ? "critical" : "neutral"}>{job.status}</Badge></div>
      <p>{job.eventType || job.errorType || "Source ingest attempt"}</p>
      {job.errorMessage && <div className="uspo-error-detail">{job.errorType}: {job.errorMessage}</div>}
      <div className="uspo-card-meta"><span>{job.operation || "No operation"}{job.duplicate ? " · duplicate" : ""}</span><span>{formatTime(job.updatedAt)}</span></div>
    </article>
  ))}</div>;
}

function ReconciliationList({ runs, selectedId, onSelect, detail, loading }) {
  return (
    <div className="uspo-split">
      <div className="uspo-list">
        {!runs.length && <div className="uspo-empty">No nightly reconciliation runs recorded.</div>}
        {runs.map((run) => {
          const runId = String(run.PK || "").replace("RECONCILIATION#", "");
          return (
            <button className={`uspo-run${selectedId === runId ? " selected" : ""}`} key={runId} onClick={() => onSelect(runId)}>
              <div className="uspo-card-heading"><strong>{formatTime(run.startedAt)}</strong><Badge tone={run.status === "failed" ? "critical" : "neutral"}>{run.status}</Badge></div>
              <div className="uspo-card-meta"><span>{run.topicsReviewed || 0} topics · {run.decisionCount || 0} decisions</span><span>{run.mutationCount || 0} mutations</span></div>
            </button>
          );
        })}
      </div>
      <section className="uspo-detail">
        {loading ? <div className="placeholder">Loading...</div> : !detail ? <div className="uspo-empty">Select a run to inspect Luna's persisted decisions.</div> : (
          <>
            <span className="uspo-eyebrow">Nightly shadow review</span>
            <h2>{formatTime(detail.run.startedAt)}</h2>
            <p className="uspo-summary">{detail.run.model || "Model not recorded"} · {detail.run.promptVersion || "Prompt version not recorded"}</p>
            <div className="uspo-history">
              <h3>Decisions</h3>
              {!detail.decisions.length && <div className="uspo-empty compact">No decisions recorded.</div>}
              {detail.decisions.map((decision) => (
                <div className="uspo-revision" key={decision.SK}>
                  <div><strong>{decision.operation || "decision"}</strong><span>{decision.decisionId || decision.SK}</span></div>
                  <p>{decision.modelReason || "No model reason recorded"}</p>
                  <span>{decision.topicId || (decision.topicIds || []).join(", ") || "No topic mutation"}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function WakePromptEditor({ prompt, onChange, onSave, onRestore, onCancelRestore }) {
  const versions = [...prompt.versions].sort((left, right) => right.version - left.version);
  const tokenEstimate = Math.ceil(prompt.value.length / 4);

  return (
    <section className="uspo-prompt" aria-labelledby="uspo-wake-prompt-heading">
      <div className="uspo-prompt-intro">
        <div>
          <h2 id="uspo-wake-prompt-heading">Wake Prompt</h2>
          <p>Controls the context Uspořadatel provides to Anna. Changes apply to subsequent initiative wakes.</p>
        </div>
        {prompt.activeVersion > 0 && <span className="uspo-prompt-version">v{prompt.activeVersion}</span>}
      </div>
      <div className="uspo-operational-note">
        Uspořadatel evaluates at most 100 distinct active topics per wake. If more exist, Anna receives a truncation indicator.
      </div>
      {prompt.error && <div className="uspo-error" role="alert">{prompt.error}</div>}
      <div className="uspo-prompt-counts" aria-live="polite">
        <span>{prompt.value.length.toLocaleString()} chars</span>
        <span aria-hidden="true">·</span>
        <span>~{tokenEstimate.toLocaleString()} tokens</span>
      </div>
      <textarea
        className="uspo-prompt-editor"
        value={prompt.value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Enter the Usporadatel wake prompt..."
        spellCheck={false}
        disabled={prompt.busy}
        aria-label="Usporadatel wake prompt"
      />
      <div className="uspo-prompt-actions">
        <button className="btn-primary" type="button" onClick={onSave} disabled={prompt.busy}>
          {prompt.operation === "save" ? "Saving..." : "Save as New Version"}
        </button>
        {prompt.saved && <span className="uspo-save-confirm" role="status">Saved as v{prompt.activeVersion}</span>}
        {prompt.updatedAt && <span className="uspo-prompt-updated">Last updated: {formatTime(prompt.updatedAt)}</span>}
      </div>

      {versions.length > 0 && (
        <div className="uspo-prompt-history">
          <h3>Version History</h3>
          <div className="uspo-version-list">
            {versions.map((version) => {
              const pendingRestore = prompt.restoreVersion === version.version;
              return (
                <div className={`uspo-version${version.version === prompt.activeVersion ? " active" : ""}`} key={version.version}>
                  <div className="uspo-version-info">
                    <span className="uspo-version-number">v{version.version}</span>
                    {version.version === prompt.activeVersion && <span className="uspo-version-active">Active</span>}
                    <span>{version.createdAt ? formatTime(version.createdAt) : "Not dated"}</span>
                    <span>{(version.charCount || 0).toLocaleString()} chars · ~{Math.ceil((version.charCount || 0) / 4).toLocaleString()} tokens</span>
                  </div>
                  <div className="uspo-version-preview">{version.preview || "No preview available"}</div>
                  {version.version !== prompt.activeVersion && !pendingRestore && (
                    <button className="uspo-restore-button" type="button" onClick={() => onRestore(version.version, false)} disabled={prompt.busy}>Restore</button>
                  )}
                  {pendingRestore && (
                    <div className="uspo-restore-confirm" role="group" aria-label={`Restore version ${version.version}`}>
                      <span>Restore v{version.version} as the active prompt?</span>
                      <button className="btn-secondary btn-xs" type="button" onClick={onCancelRestore} disabled={prompt.busy}>Cancel</button>
                      <button className="btn-primary btn-xs" type="button" onClick={() => onRestore(version.version, true)} disabled={prompt.busy}>
                        {prompt.operation === "restore" ? "Restoring..." : "Confirm restore"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default function Usporadatel({ topicsOnly = false, active = true, onUnreadCount, initialTopicId }) {
  const [activeTab, setActiveTab] = useState("topics");
  const [data, setData] = useState({ topics: [], jobs: [], reconciliations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [topicDetail, setTopicDetail] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const topicRequestRef = useRef(0);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [topicFilter, setTopicFilter] = useState("open");
  const [wakePrompt, setWakePrompt] = useState({
    value: "",
    activeVersion: 0,
    updatedAt: "",
    versions: [],
    busy: false,
    operation: "",
    error: "",
    saved: false,
    restoreVersion: null,
  });

  const visibleTopics = filterTopics(data.topics, topicFilter);
  const topicCounts = Object.fromEntries(
    TOPIC_FILTERS.map((filter) => [filter.id, filterTopics(data.topics, filter.id).length]),
  );

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    setError("");
    if (activeTab === "wake-prompt") {
      setWakePrompt((current) => ({ ...current, error: "", saved: false }));
    }
    try {
      if (activeTab === "wake-prompt") {
        const response = await usporadatelApi.getWakePrompt();
        setWakePrompt((current) => ({
          ...current,
          value: response.prompt || "",
          activeVersion: response.activeVersion || 0,
          updatedAt: response.updatedAt || "",
          versions: response.versions || [],
          error: "",
          restoreVersion: null,
        }));
        return;
      }
      const response = activeTab === "topics" ? await usporadatelApi.listTopics()
        : activeTab === "jobs" ? await usporadatelApi.listJobs()
          : await usporadatelApi.listReconciliations();
      setData((current) => ({ ...current, [activeTab]: response[activeTab === "reconciliations" ? "runs" : activeTab] || [] }));
    } catch (loadError) {
      console.error("Failed to load Usporadatel:", loadError);
      if (activeTab === "wake-prompt") {
        setWakePrompt((current) => ({ ...current, error: "The wake prompt could not be loaded. Try refreshing." }));
      } else {
        setError("Usporadatel data could not be refreshed.");
      }
    } finally {
      setLoading(false);
    }
  }, [active, activeTab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    onUnreadCount?.(filterTopics(data.topics, "new").length);
  }, [data.topics, onUnreadCount]);

  const selectTopic = useCallback(async (topicId) => {
    const generation = ++topicRequestRef.current;
    setSelectedTopicId(topicId);
    setDetailLoading(true);
    try {
      const [detailResponse, revisionResponse] = await Promise.all([usporadatelApi.getTopic(topicId), usporadatelApi.getRevisions(topicId)]);
      if (generation !== topicRequestRef.current) return;
      let topic = detailResponse.topic;
      setTopicDetail(topic);
      setRevisions(revisionResponse.revisions || []);
      if (topic.unread) {
        const acknowledgement = await usporadatelApi.markTopicRead(topicId, topic.version);
        topic = acknowledgement.topic;
        window.dispatchEvent(new CustomEvent("anna:topic-read", { detail: topic }));
        if (generation !== topicRequestRef.current) return;
        setTopicDetail(topic);
        setData((current) => ({
          ...current,
          topics: current.topics.map((item) => (item.topicId === topicId ? topic : item)),
        }));
      }
    } catch (loadError) {
      console.error("Failed to load Usporadatel topic detail:", loadError);
      if (generation === topicRequestRef.current) setError("Topic detail could not be loaded.");
    } finally {
      if (generation === topicRequestRef.current) setDetailLoading(false);
    }
  }, []);

  useEffect(() => () => { topicRequestRef.current += 1; }, []);

  useEffect(() => {
    if (active && initialTopicId) selectTopic(initialTopicId);
  }, [active, initialTopicId, selectTopic]);

  const selectRun = async (runId) => {
    setSelectedRunId(runId);
    setDetailLoading(true);
    try {
      setRunDetail(await usporadatelApi.getReconciliation(runId));
    } catch (loadError) {
      console.error("Failed to load reconciliation detail:", loadError);
      setError("Reconciliation detail could not be loaded.");
    } finally {
      setDetailLoading(false);
    }
  };

  const changeTopicFilter = (filter) => {
    setTopicFilter(filter);
    setSelectedTopicId(null);
    setTopicDetail(null);
    setRevisions([]);
  };

  const saveWakePrompt = async () => {
    setWakePrompt((current) => ({ ...current, busy: true, operation: "save", error: "", saved: false }));
    try {
      const saved = await usporadatelApi.saveWakePrompt(wakePrompt.value);
      const refreshed = await usporadatelApi.getWakePrompt();
      setWakePrompt((current) => ({
        ...current,
        value: refreshed.prompt || current.value,
        activeVersion: refreshed.activeVersion || saved.activeVersion || saved.version || 0,
        updatedAt: refreshed.updatedAt || saved.updatedAt || "",
        versions: refreshed.versions || [],
        busy: false,
        operation: "",
        saved: true,
      }));
    } catch (saveError) {
      console.error("Failed to save Usporadatel wake prompt:", saveError);
      setWakePrompt((current) => ({ ...current, busy: false, operation: "", error: "The wake prompt could not be saved. Your edits are still here." }));
    }
  };

  const restoreWakePrompt = async (version, confirmed) => {
    if (!confirmed) {
      setWakePrompt((current) => ({ ...current, restoreVersion: version, error: "", saved: false }));
      return;
    }
    setWakePrompt((current) => ({ ...current, busy: true, operation: "restore", error: "", saved: false }));
    try {
      await usporadatelApi.restoreWakePrompt(version);
      const refreshed = await usporadatelApi.getWakePrompt();
      setWakePrompt((current) => ({
        ...current,
        value: refreshed.prompt || "",
        activeVersion: refreshed.activeVersion || version,
        updatedAt: refreshed.updatedAt || "",
        versions: refreshed.versions || [],
        busy: false,
        operation: "",
        restoreVersion: null,
      }));
    } catch (restoreError) {
      console.error("Failed to restore Usporadatel wake prompt:", restoreError);
      setWakePrompt((current) => ({ ...current, busy: false, operation: "", error: `Version ${version} could not be restored.` }));
    }
  };

  return (
    <div className={`usporadatel-page${topicsOnly ? " embedded" : ""}`} hidden={!active}>
      <header className="uspo-header">
        <div>{!topicsOnly && <span className="uspo-eyebrow">Agent observability</span>}<h1>{topicsOnly ? "Topics" : "Uspořadatel"}</h1></div>
        <RefreshButton onClick={load} busy={loading || wakePrompt.busy} title={topicsOnly ? "Refresh topics" : REFRESH_TITLES[activeTab]} />
      </header>
      {!topicsOnly && <nav className="tab-bar uspo-tabs" aria-label="Uspořadatel observability views">
        {TABS.map((tab) => <button className={`tab${activeTab === tab.id ? " active" : ""}`} key={tab.id} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>)}
      </nav>}
      {error && <div className="uspo-error" role="alert">{error}</div>}
      {loading ? <div className="placeholder">Loading...</div> : (topicsOnly || activeTab === "topics") ? (
        <>
          <div className="uspo-topic-filters" role="group" aria-label="Filter topics">
            {TOPIC_FILTERS.map((filter) => (
              <button
                className={topicFilter === filter.id ? "active" : ""}
                key={filter.id}
                onClick={() => changeTopicFilter(filter.id)}
                type="button"
              >
                {filter.label}<span>{topicCounts[filter.id]}</span>
              </button>
            ))}
          </div>
          <div className="uspo-split">
            <div className="uspo-list">{visibleTopics.length === 0 ? <div className="uspo-empty">No {topicFilter === "all" ? "" : `${topicFilter} `}topics.</div> : visibleTopics.map((topic) => <TopicCard key={topic.topicId} item={topic} selected={selectedTopicId === topic.topicId} onSelect={() => selectTopic(topic.topicId)} />)}</div>
            <TopicDetail detail={topicDetail} revisions={revisions} loading={detailLoading} />
          </div>
        </>
      ) : activeTab === "jobs" ? <JobList jobs={data.jobs} />
        : activeTab === "reconciliations" ? <ReconciliationList runs={data.reconciliations} selectedId={selectedRunId} onSelect={selectRun} detail={runDetail} loading={detailLoading} />
          : <WakePromptEditor
            prompt={wakePrompt}
            onChange={(value) => setWakePrompt((current) => ({ ...current, value, saved: false }))}
            onSave={saveWakePrompt}
            onRestore={restoreWakePrompt}
            onCancelRestore={() => setWakePrompt((current) => ({ ...current, restoreVersion: null }))}
          />}
    </div>
  );
}